import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, FeatureGroup } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import * as L from "leaflet";
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Snackbar,
} from "@mui/material";
import { Save, RestartAlt } from "@mui/icons-material";
import CommonTable from "../../../common/CommonTable";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "./ForecastValueAddition.css";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || "";



// ── Constants ─────────────────────────────────────────────────────────────────

const SOURCES = ["GFS", "ICON", "METOFFICE", "ECMWF-IFS"];

const ALERT_COLORS = {
    green: "rgb(148, 203, 85)",
    yellow: "rgb(243, 235, 18)",
    orange: "rgb(243, 155, 58)",
    red: "rgb(230, 45, 58)",
    grey: "rgb(222, 222, 223)",
};

const PARAM_RANGES = {
    rainfall: [
        { max: 15, color: ALERT_COLORS.green },
        { max: 25, color: ALERT_COLORS.yellow },
        { max: 60, color: ALERT_COLORS.orange },
        { max: Infinity, color: ALERT_COLORS.red },
    ],
    max_temp: [
        { max: 20, color: ALERT_COLORS.green },
        { max: 30, color: ALERT_COLORS.yellow },
        { max: 40, color: ALERT_COLORS.orange },
        { max: Infinity, color: ALERT_COLORS.red },
    ],
    min_temp: [
        { max: 0, color: ALERT_COLORS.red },
        { max: 10, color: ALERT_COLORS.orange },
        { max: 20, color: ALERT_COLORS.yellow },
        { max: Infinity, color: ALERT_COLORS.green },
    ],
    relative_humidity: [
        { max: 40, color: ALERT_COLORS.green },
        { max: 60, color: ALERT_COLORS.yellow },
        { max: 80, color: ALERT_COLORS.orange },
        { max: Infinity, color: ALERT_COLORS.red },
    ],
    // Same thresholds as Forecastmap.js's wind_speed ranges, for consistency.
    wind_speed: [
        { max: 40, color: ALERT_COLORS.green },
        { max: 60, color: ALERT_COLORS.yellow },
        { max: 80, color: ALERT_COLORS.orange },
        { max: Infinity, color: ALERT_COLORS.red },
    ],
};

const getBadgeColor = (paramId, value, apiColor) => {
    if (value === null || value === undefined || value === "") return ALERT_COLORS.grey;
    const ranges = PARAM_RANGES[paramId];
    if (ranges) {
        const num = Number(value);
        for (const { max, color } of ranges) {
            if (num < max) return color;
        }
    }
    return apiColor || ALERT_COLORS.green;
};

const PARAMETERS = [
    { id: "rainfall", label: "Rainfall (mm)", variable: "tp" },
    { id: "max_temp", label: "Max Temperature (°C)", variable: "heat" },
    { id: "min_temp", label: "Min Temperature (°C)", variable: "cold" },
    { id: "relative_humidity", label: "Relative Humidity (%)", variable: "rh" },
    { id: "wind_speed", label: "Wind Speed (km/h)", variable: "wind" },
];

const LEVEL_TABS = [
    { value: "suco", label: "Suco", level: 3, geoKey: "adm3" },
    { value: "submunicipality", label: "Sub Municipality", level: 2, geoKey: "adm2" },
    { value: "municipality", label: "Municipality", level: 1, geoKey: "adm1" },
];

const GEOJSON_URLS = {
    adm1: "/DATA/OSMbased_adm1_boundary.json",
    adm2: "/DATA/OSMbased_adm2_boundary.json",
    adm3: "/DATA/OSMbased_adm3_boundary.json",
};

// ── Utility helpers ───────────────────────────────────────────────────────────

const cleanGeoJSON = (data) => {
    if (!data?.features) return data;
    return {
        ...data,
        features: data.features.map((feature) => {
            if (
                feature.geometry?.type === "MultiPolygon" &&
                Array.isArray(feature.geometry.coordinates?.[0]?.[0]?.[0]?.[0])
            ) {
                return {
                    ...feature,
                    geometry: {
                        ...feature.geometry,
                        coordinates: feature.geometry.coordinates.map((poly) => poly[0]),
                    },
                };
            }
            return feature;
        }),
    };
};

const ringCentroid = (ring) => {
    let area = 0, cx = 0, cy = 0;
    for (let i = 0; i < ring.length - 1; i++) {
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[i + 1];
        const cross = x1 * y2 - x2 * y1;
        area += cross;
        cx += (x1 + x2) * cross;
        cy += (y1 + y2) * cross;
    }
    area /= 2;
    if (!area) return null;
    return { lat: cy / (6 * area), lng: cx / (6 * area), area: Math.abs(area) };
};

const getCenterForFeatures = (features) => {
    let best = null;
    features.forEach((feature) => {
        const geom = feature.geometry;
        if (!geom) return;
        const rings =
            geom.type === "Polygon"
                ? [geom.coordinates[0]]
                : geom.type === "MultiPolygon"
                ? geom.coordinates.map((poly) => poly[0])
                : [];
        rings.forEach((ring) => {
            if (!ring || ring.length < 4) return;
            const centroid = ringCentroid(ring);
            if (centroid && (!best || centroid.area > best.area)) best = centroid;
        });
    });
    return best ? [best.lat, best.lng] : null;
};

const formatDayLabel = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};


const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    const n = Number(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// Ray-cast point-in-polygon: center=[lat,lng], geoJsonPolygon = GeoJSON Polygon geometry
const isPointInGeoJSONPolygon = (center, geoJsonPolygon) => {
    if (!center || !geoJsonPolygon?.coordinates?.[0]) return false;
    const [lat, lng] = center;
    const ring = geoJsonPolygon.coordinates[0]; // [[lng, lat], ...]
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i];
        const [xj, yj] = ring[j];
        const intersect = ((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
};

// Build table rows from geoData + source data at a given day index
const buildRows = (geoData, data, dayIndex) => {
    if (!geoData?.features) return [];
    const byCode = new Map();
    geoData.features.forEach((f) => {
        const code = f.properties?.ADM_Code?.toString();
        if (!code) return;
        if (!byCode.has(code)) byCode.set(code, []);
        byCode.get(code).push(f);
    });
    const result = [];
    byCode.forEach((features, admCode) => {
        const props = features[0].properties;
        const records = data?.[admCode] || [];
        const record = records[dayIndex] || null;
        result.push({
            admCode,
            municipality: props.ADM1 || "-",
            subMunicipality: props.ADM2 || "-",
            suco: props.ADM3 || "-",
            value: record?.forecast_value_added ?? record?.aggregate_value ?? null,
            apiColor: Array.isArray(record?.color) ? record.color[0] : null,
            center: getCenterForFeatures(features),
        });
    });
    return result;
};

// ── Legend ────────────────────────────────────────────────────────────────────

const LEGEND_ITEMS = [
    { color: ALERT_COLORS.green, label: "Low" },
    { color: ALERT_COLORS.yellow, label: "Medium" },
    { color: ALERT_COLORS.orange, label: "High" },
    { color: ALERT_COLORS.red, label: "Very High" },
    { color: ALERT_COLORS.grey, label: "No Data" },
];

const AlertLegend = () => (
    <div className="fva-legend">
        {LEGEND_ITEMS.map(({ color, label }) => (
            <div key={label} className="fva-legend-item">
                <div className="fva-legend-swatch" style={{ background: color }} />
                <span>{label}</span>
            </div>
        ))}
    </div>
);

// ── Mini source map (comparison grid cell) — colored polygons + hover tooltip ─

const SourceMiniMap = memo(({ source, geoData, rows, paramId, loading, errorMsg, initTime, mapKey, geoDataKey }) => {
    const rowsByCode = useMemo(() => new Map(rows.map((r) => [r.admCode, r])), [rows]);

    const geoStyle = useCallback(
        (feature) => {
            const admCode = feature.properties?.ADM_Code?.toString();
            const row = rowsByCode.get(admCode);
            const val = row?.value;
            const hasVal = val !== null && val !== undefined;
            const color = hasVal ? getBadgeColor(paramId, val, row?.apiColor) : ALERT_COLORS.grey;
            return {
                fillColor: color,
                fillOpacity: 1,
                color: "#374151",
                weight: 1,
                opacity: 1,
            };
        },
        [rowsByCode, paramId]
    );

    const onEachFeature = useCallback(
        (feature, layer) => {
            const admCode = feature.properties?.ADM_Code?.toString();
            const row = rowsByCode.get(admCode);
            const props = feature.properties;
            const name = props.ADM3 || props.ADM2 || props.ADM1 || admCode;
            const val = row?.value;
            const valStr = val !== null && val !== undefined ? fmtVal(val) : "N/A";
            const pLabel = PARAMETERS.find((p) => p.id === paramId)?.label || paramId;
            layer.bindTooltip(
                `${name} - ${pLabel}: ${valStr}`,
                { permanent: false, sticky: true, direction: "auto", offset: [0, 0], className: "custom-tooltip" }
            );
        },
        [rowsByCode, paramId]
    );

    return (
        <div className="fva-source-card">
            <div className="fva-source-card-header">
                <span className="fva-source-label">{source}</span>
                {initTime && <span className="fva-source-init">{initTime}</span>}
            </div>
            <div className="fva-mini-map-wrapper">
                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <CircularProgress size={26} />
                    </Box>
                ) : errorMsg ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 2, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>No data available</Typography>
                    </Box>
                ) : geoData ? (
                    <MapContainer
                        key={mapKey}
                        zoom={8}
                        center={[-8.874217, 125.727539]}
                        zoomControl={false}
                        scrollWheelZoom={false}
                        attributionControl={false}
                        style={{ height: "100%", width: "100%" }}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <GeoJSON key={geoDataKey} data={geoData} style={geoStyle} onEachFeature={onEachFeature} />
                    </MapContainer>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <Typography sx={{ fontSize: "12px", color: "#cbd5e1" }}>Loading boundary...</Typography>
                    </Box>
                )}
            </div>
        </div>
    );
});

// ── Value Addition Map (filled polygons + draw tool) ─────────────────────────

const ValueAdditionMap = memo(({ geoData, rows, paramId, geoKey, featureGroupRef, onDrawCreated, onDrawDeleted, selectionCodes }) => {
    const rowsByCode = useMemo(() => new Map(rows.map((r) => [r.admCode, r])), [rows]);

    const geoStyle = useCallback(
        (feature) => {
            const admCode = feature.properties?.ADM_Code?.toString();
            const row = rowsByCode.get(admCode);
            const val = row?.displayValue;
            const hasVal = val !== null && val !== undefined && val !== "";
            const color = hasVal ? getBadgeColor(paramId, val, row?.apiColor) : ALERT_COLORS.grey;
            const isSelected = !selectionCodes || selectionCodes.has(admCode);
            return {
                fillColor: color,
                fillOpacity: isSelected ? (hasVal ? 0.75 : 0.2) : 0.1,
                color: "#1d4ed8",
                weight: isSelected ? 1.5 : 0.8,
                opacity: isSelected ? 1 : 0.4,
            };
        },
        [rowsByCode, paramId, selectionCodes]
    );

    if (!geoData) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="fva-map-wrapper">
            <MapContainer
                key="va-main-map"
                zoom={9}
                center={[-8.814217, 125.727539]}
                zoomControl={true}
                scrollWheelZoom={true}
                style={{ height: "100%", width: "100%" }}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <GeoJSON key={geoKey} data={geoData} style={geoStyle} />
                {rows.map((row) => {
                    if (!row.center) return null;
                    const val = row.displayValue;
                    const hasVal = val !== null && val !== undefined && val !== "";
                    const bg = hasVal ? getBadgeColor(paramId, val, row.apiColor) : ALERT_COLORS.grey;
                    const isSelected = !selectionCodes || selectionCodes.has(row.admCode);
                    if (!isSelected) return null;
                    return (
                        <Marker
                            key={row.admCode}
                            position={row.center}
                            icon={L.divIcon({
                                className: "fva-value-badge-wrapper",
                                html: `<div class="fva-value-badge" style="background:${bg};">${hasVal ? fmtVal(val) : "-"}</div>`,
                                iconSize: [28, 20],
                                iconAnchor: [14, 10],
                            })}
                        />
                    );
                })}
                <FeatureGroup ref={featureGroupRef}>
                    <EditControl
                        position="topright"
                        onCreated={onDrawCreated}
                        onDeleted={onDrawDeleted}
                        draw={{
                            polygon: true,
                            rectangle: true,
                            circle: false,
                            circlemarker: false,
                            marker: false,
                            polyline: false,
                        }}
                        edit={{ edit: false }}
                    />
                </FeatureGroup>
            </MapContainer>
            <AlertLegend />
        </div>
    );
});

// ── Main component ────────────────────────────────────────────────────────────

const ForecastValueAddition = () => {
    // ── Comparison grid state ─────────────────────────────────────────────────
    const [adminLevelTab, setAdminLevelTab] = useState("suco");
    const [selectedParameter, setSelectedParameter] = useState("rainfall");
    const [selectedDayIndex, setSelectedDayIndex] = useState(0);

    // ── Shared geo cache ──────────────────────────────────────────────────────
    const [geoCache, setGeoCache] = useState({});
    const geoLoadedRef = useRef(new Set());

    // ── Per-source comparison data { GFS: { latest_init_time, data }, ... } ──
    const [sourceData, setSourceData] = useState({});
    const [sourceLoading, setSourceLoading] = useState({});
    const [sourceError, setSourceError] = useState({});

    // ── Value addition section ────────────────────────────────────────────────
    const [vaExpanded, setVaExpanded] = useState(false);
    // No longer user-selectable here — mirrors whichever source is
    // currently active in Data Management (fetched below), since raw and
    // value-added are always the same pair now.
    const [vaSource, setVaSource] = useState("ECMWF-IFS");
    const [vaData, setVaData] = useState(null);
    const [vaLoading, setVaLoading] = useState(false);
    const [vaError, setVaError] = useState(null);
    const [vaSelectedDate, setVaSelectedDate] = useState("");
    const [vaOverrides, setVaOverrides] = useState({});
    const [vaSaving, setVaSaving] = useState(false);
    const [vaGeoRefreshKey, setVaGeoRefreshKey] = useState(0);
    // Each drawn polygon: { id, codes: Set<admCode>, op: null|"add"|"sub", value: string }
    const [vaPolygons, setVaPolygons] = useState([]);
    const [vaApplyAll, setVaApplyAll] = useState(false); // false = per-polygon, true = same op/value for all
    const [vaAllOp, setVaAllOp] = useState(null); // null | "add" | "sub"
    const [vaAllValue, setVaAllValue] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const vaFeatureGroupRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const stableOrderRef = useRef([]);
    const currentDisplayRowsRef = useRef([]);

    // Derived config — VA inherits from top-level controls
    const activeLevelConfig = LEVEL_TABS.find((t) => t.value === adminLevelTab) || LEVEL_TABS[0];
    const activeParamConfig = PARAMETERS.find((p) => p.id === selectedParameter) || PARAMETERS[0];

    // ── GeoJSON loader (ref-guarded to avoid duplicate fetches) ───────────────
    const loadGeo = useCallback(async (geoKey) => {
        if (geoLoadedRef.current.has(geoKey)) return;
        geoLoadedRef.current.add(geoKey);
        try {
            const res = await fetch(GEOJSON_URLS[geoKey]);
            if (!res.ok) throw new Error("Failed to load boundary");
            const raw = await res.json();
            setGeoCache((prev) => ({ ...prev, [geoKey]: cleanGeoJSON(raw) }));
        } catch (err) {
            geoLoadedRef.current.delete(geoKey); // allow retry
            console.error("GeoJSON error:", geoKey, err);
        }
    }, []);

    useEffect(() => { loadGeo(activeLevelConfig.geoKey); }, [activeLevelConfig.geoKey, loadGeo]);
    useEffect(() => { if (vaExpanded) loadGeo(activeLevelConfig.geoKey); }, [vaExpanded, activeLevelConfig.geoKey, loadGeo]);

    // Value Addition's source is whatever Data Management currently has
    // active — same forecastmodels/set-source GET DataManagement.js reads —
    // so this section always edits the source that's actually live, without
    // its own picker.
    useEffect(() => {
        const fetchActiveSource = async () => {
            try {
                const res = await fetch(`${backendUrl}forecastmodels/set-source`);
                const data = await res.json();
                if (res.ok && data.active_value_added_source) {
                    // active_value_added_source comes back as e.g. "ECMWF-IFS-VA" —
                    // strip the "-VA" suffix to match the bare names in SOURCES/
                    // the value-addition API's `source` query param.
                    setVaSource(data.active_value_added_source.toUpperCase().replace(/-VA$/, ""));
                }
            } catch (err) {
                console.error("Failed to fetch active forecast source:", err);
            }
        };
        fetchActiveSource();
    }, []);

    // Union of admCodes covered by any drawn polygon (null = no polygons drawn = all rows)
    const vaSelectionCodes = useMemo(() => {
        if (vaPolygons.length === 0) return null;
        const set = new Set();
        vaPolygons.forEach((p) => p.codes.forEach((c) => set.add(c)));
        return set.size > 0 ? set : null;
    }, [vaPolygons]);

    // Clear VA section when top-level admin level or parameter changes
    useEffect(() => {
        setVaData(null);
        setVaSelectedDate("");
        setVaOverrides({});
        setVaPolygons([]);
        setVaGeoRefreshKey((k) => k + 1);
    }, [adminLevelTab, selectedParameter]);

    // Reset editing lock when context changes (date, source, level, parameter)
    useEffect(() => {
        setIsEditing(false);
        stableOrderRef.current = [];
        setVaPolygons([]);
        setVaApplyAll(false);
        setVaAllOp(null);
        setVaAllValue("");
    }, [vaSelectedDate, vaSource, adminLevelTab, selectedParameter]);

    // ── Fetch all 4 sources for the comparison grid ───────────────────────────
    useEffect(() => {
        const variable = activeParamConfig.variable;
        const level = activeLevelConfig.level;
        setSelectedDayIndex(0);

        SOURCES.forEach(async (source) => {
            setSourceLoading((prev) => ({ ...prev, [source]: true }));
            setSourceError((prev) => ({ ...prev, [source]: null }));
            try {
                const url = `${backendUrl}forecastmodels/value-addition/?source=${source}&variable=${variable}&admin_level=${level}`;
                const res = await fetch(url);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setSourceData((prev) => ({ ...prev, [source]: json }));
            } catch (err) {
                setSourceError((prev) => ({ ...prev, [source]: err.message }));
                setSourceData((prev) => ({ ...prev, [source]: null }));
            } finally {
                setSourceLoading((prev) => ({ ...prev, [source]: false }));
            }
        });
    }, [selectedParameter, adminLevelTab, activeParamConfig.variable, activeLevelConfig.level]);

    // ── Rows per source for the comparison grid ───────────────────────────────
    const activeGeo = geoCache[activeLevelConfig.geoKey];

    const sourceRows = useMemo(() => {
        const result = {};
        SOURCES.forEach((source) => {
            result[source] = buildRows(activeGeo, sourceData[source]?.data, selectedDayIndex);
        });
        return result;
    }, [activeGeo, sourceData, selectedDayIndex]);

    // ── Value addition: load data ─────────────────────────────────────────────
    const handleVaSubmit = useCallback(async () => {
        const variable = activeParamConfig.variable;
        const level = activeLevelConfig.level;
        setVaLoading(true);
        setVaError(null);
        setVaData(null);
        setVaSelectedDate("");
        setVaOverrides({});
        setVaPolygons([]);
        try {
            const url = `${backendUrl}forecastmodels/value-addition/?source=${vaSource}&variable=${variable}&admin_level=${level}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setVaData(json);
            const allDates = new Set();
            Object.values(json.data || {}).forEach((recs) => {
                recs.forEach((r) => { if (r.forecast_date) allDates.add(r.forecast_date); });
            });
            const sorted = Array.from(allDates).sort();
            if (sorted.length > 0) setVaSelectedDate(sorted[0]);
            setVaGeoRefreshKey((k) => k + 1);
            loadGeo(activeLevelConfig.geoKey);
        } catch (err) {
            setVaError(err.message);
        } finally {
            setVaLoading(false);
        }
    }, [vaSource, activeParamConfig.variable, activeLevelConfig.level, activeLevelConfig.geoKey, loadGeo]);

    // Auto-fetch VA data whenever the section is open or source/level/parameter changes
    useEffect(() => {
        if (!vaExpanded) return;
        handleVaSubmit();
    }, [vaExpanded, handleVaSubmit]);

    // Available forecast dates in loaded VA data
    const vaAvailableDates = useMemo(() => {
        if (!vaData?.data) return [];
        const s = new Set();
        Object.values(vaData.data).forEach((recs) => {
            recs.forEach((r) => { if (r.forecast_date) s.add(r.forecast_date); });
        });
        return Array.from(s).sort();
    }, [vaData]);

    const vaGeo = geoCache[activeLevelConfig.geoKey];

    // ── Build editable VA rows for the selected date ──────────────────────────
    const vaRows = useMemo(() => {
        if (!vaGeo?.features || !vaData?.data || !vaSelectedDate) return [];
        const byCode = new Map();
        vaGeo.features.forEach((f) => {
            const code = f.properties?.ADM_Code?.toString();
            if (!code) return;
            if (!byCode.has(code)) byCode.set(code, []);
            byCode.get(code).push(f);
        });
        const result = [];
        byCode.forEach((features, admCode) => {
            const props = features[0].properties;
            const records = vaData.data[admCode] || [];
            const record = records.find((r) => r.forecast_date === vaSelectedDate) || null;
            const rawValue = record?.aggregate_value ?? null;
            const savedValue = record?.forecast_value_added ?? null;
            const overrideKey = `${vaSource}_${adminLevelTab}_${selectedParameter}_${admCode}_${vaSelectedDate}`;
            const overrideVal = vaOverrides[overrideKey];
            const displayValue = overrideVal !== undefined ? overrideVal : (savedValue ?? rawValue);
            result.push({
                admCode,
                overrideKey,
                municipality: props.ADM1 || "-",
                subMunicipality: props.ADM2 || "-",
                suco: props.ADM3 || "-",
                rawValue,
                savedValue,
                displayValue,
                apiColor: Array.isArray(record?.color) ? record.color[0] : null,
                valueAddedAt: record?.value_added_at || null,
                center: getCenterForFeatures(features),
            });
        });
        const toNum = (v) => (v !== null && v !== undefined && v !== "" ? Number(v) : -Infinity);
        return result.sort((a, b) => toNum(b.displayValue) - toNum(a.displayValue));
    }, [vaGeo, vaData, vaSelectedDate, vaSource, adminLevelTab, selectedParameter, vaOverrides]);

    const hasCurrentOverrides = useMemo(
        () => vaRows.some((r) => vaOverrides[r.overrideKey] !== undefined),
        [vaRows, vaOverrides]
    );

    // ── Save edited values to DB ──────────────────────────────────────────────
    const handleVaSave = useCallback(async () => {
        const variable = activeParamConfig.variable;
        const level = activeLevelConfig.level;
        const updates = vaRows
            .filter((r) => vaOverrides[r.overrideKey] !== undefined)
            .map((r) => ({ pcode: r.admCode, value: vaOverrides[r.overrideKey] }));

        if (!updates.length) return;
        setVaSaving(true);
        try {
            const res = await fetch(`${backendUrl}forecastmodels/value-addition/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    source: vaSource,
                    variable,
                    admin_level: level,
                    forecast_date: vaSelectedDate,
                    updates,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result = await res.json();
            setSnackbar({
                open: true,
                message: `Saved ${result.updated} record(s) at ${result.value_added_at}`,
                severity: "success",
            });
            // Refresh VA data
            const url = `${backendUrl}forecastmodels/value-addition/?source=${vaSource}&variable=${variable}&admin_level=${level}`;
            const refreshRes = await fetch(url);
            if (refreshRes.ok) setVaData(await refreshRes.json());
            // Clear overrides for saved pcodes
            setVaOverrides((prev) => {
                const next = { ...prev };
                updates.forEach((u) => {
                    delete next[`${vaSource}_${adminLevelTab}_${selectedParameter}_${u.pcode}_${vaSelectedDate}`];
                });
                return next;
            });
            setVaGeoRefreshKey((k) => k + 1);
        } catch (err) {
            setSnackbar({ open: true, message: `Save failed: ${err.message}`, severity: "error" });
        } finally {
            setVaSaving(false);
        }
    }, [vaRows, vaOverrides, vaSource, activeParamConfig.variable, activeLevelConfig.level, adminLevelTab, selectedParameter, vaSelectedDate]);

    // ── Reset edited overrides ────────────────────────────────────────────────
    const handleVaReset = useCallback(() => {
        setVaOverrides({});
        setIsEditing(false);
        stableOrderRef.current = [];
        setVaPolygons((prev) => prev.map((p) => ({ ...p, op: null, value: "" })));
        setVaApplyAll(false);
        setVaAllOp(null);
        setVaAllValue("");
        setVaGeoRefreshKey((k) => k + 1);
    }, []);

    // ── Per-polygon Add/Sub op + value toggles ────────────────────────────────
    const handleVaPolygonOpToggle = useCallback((polyId, op) => {
        setVaPolygons((prev) =>
            prev.map((p) => (p.id === polyId ? { ...p, op: p.op === op ? null : op } : p))
        );
    }, []);

    const handleVaPolygonValueChange = useCallback((polyId, value) => {
        setVaPolygons((prev) => prev.map((p) => (p.id === polyId ? { ...p, value } : p)));
    }, []);

    const handleVaAllOpToggle = useCallback((op) => {
        setVaAllOp((prev) => (prev === op ? null : op));
    }, []);

    // Whether there's at least one polygon with a usable op+value configured
    const hasPendingPolygonUpdates = useMemo(() => {
        if (vaPolygons.length === 0) return false;
        if (vaApplyAll) return Boolean(vaAllOp) && vaAllValue !== "";
        return vaPolygons.some((p) => Boolean(p.op) && p.value !== "");
    }, [vaPolygons, vaApplyAll, vaAllOp, vaAllValue]);

    // Apply each polygon's Add/Sub value (or the shared "apply to all" value) to its rows
    const handleVaPolygonUpdate = useCallback(() => {
        if (vaPolygons.length === 0) return;
        setVaOverrides((prev) => {
            const next = { ...prev };
            vaPolygons.forEach((poly) => {
                const op = vaApplyAll ? vaAllOp : poly.op;
                const rawValue = vaApplyAll ? vaAllValue : poly.value;
                if (!op || rawValue === "" || rawValue === undefined) return;
                const amount = Number(rawValue);
                if (Number.isNaN(amount)) return;
                const sign = op === "sub" ? -1 : 1;
                vaRows.forEach((row) => {
                    if (!poly.codes.has(row.admCode)) return;
                    const currentOverride = next[row.overrideKey];
                    const base =
                        currentOverride !== undefined
                            ? Number(currentOverride)
                            : row.displayValue !== null && row.displayValue !== undefined && row.displayValue !== ""
                            ? Number(row.displayValue)
                            : 0;
                    next[row.overrideKey] = base + sign * amount;
                });
            });
            return next;
        });
        if (vaApplyAll) {
            setVaAllOp(null);
            setVaAllValue("");
        } else {
            setVaPolygons((prev) => prev.map((p) => ({ ...p, op: null, value: "" })));
        }
    }, [vaPolygons, vaApplyAll, vaAllOp, vaAllValue, vaRows]);

    // ── Draw polygon → append to the list of active polygon selections ───────
    const handleVaDrawCreated = useCallback((e) => {
        const layer = e.layer;
        const drawnGeo = layer.toGeoJSON().geometry;
        const polygons = drawnGeo.type === "Polygon"
            ? [drawnGeo]
            : drawnGeo.type === "MultiPolygon"
            ? drawnGeo.coordinates.map((c) => ({ type: "Polygon", coordinates: c }))
            : [];
        const codes = new Set();
        vaRows.forEach((row) => {
            if (row.center && polygons.some((poly) => isPointInGeoJSONPolygon(row.center, poly))) {
                codes.add(row.admCode);
            }
        });
        if (codes.size > 0) {
            const polyId = L.stamp(layer);
            setVaPolygons((prev) => [...prev, { id: polyId, codes, op: null, value: "" }]);
        }

        // Auto-restart the same draw tool so the next polygon can be drawn
        // immediately, without re-clicking the toolbar icon each time.
        const map = e.target;
        const DrawTool = e.layerType === "rectangle" ? L.Draw?.Rectangle : L.Draw?.Polygon;
        if (map && DrawTool) {
            setTimeout(() => new DrawTool(map).enable(), 0);
        }
    }, [vaRows]);

    const handleVaDrawDeleted = useCallback((e) => {
        const deletedIds = new Set();
        e.layers.eachLayer((layer) => deletedIds.add(L.stamp(layer)));
        setVaPolygons((prev) => prev.filter((p) => !deletedIds.has(p.id)));
    }, []);

    const handleVaInputFocus = useCallback(() => {
        if (!isEditing) {
            stableOrderRef.current = currentDisplayRowsRef.current.map((r) => r.admCode);
            setIsEditing(true);
        }
    }, [isEditing]);

    const handleVaInputBlur = useCallback(() => {
        setIsEditing(false);
    }, []);

    const handleVaInputKeyDown = useCallback((e) => {
        if (e.key === "Enter") {
            e.target.blur();
        }
    }, []);

    // ── Polygon-filtered rows for the table ───────────────────────────────────
    const displayVaRows = useMemo(() => {
        const filtered = vaSelectionCodes ? vaRows.filter((r) => vaSelectionCodes.has(r.admCode)) : vaRows;
        if (isEditing && stableOrderRef.current.length > 0) {
            // Lock row positions while editing — prevent re-sort on each keystroke
            const rowMap = new Map(filtered.map((r) => [r.admCode, r]));
            return stableOrderRef.current
                .filter((code) => rowMap.has(code))
                .map((code) => rowMap.get(code));
        }
        // Not editing: update the stable-order snapshot and return sorted rows
        currentDisplayRowsRef.current = filtered;
        return filtered;
    }, [vaRows, vaSelectionCodes, isEditing]);

    // ── VA table columns ──────────────────────────────────────────────────────
    const vaTableColumns = useMemo(() => {
        const cols = [
            { key: "SlNo", label: "S.No", sortable: false, align: "center" },
            { key: "municipality", label: "Municipality", sortable: true, align: "left" },
        ];
        if (adminLevelTab !== "municipality") {
            cols.push({ key: "subMunicipality", label: "Sub Municipality", sortable: true, align: "left" });
        }
        if (adminLevelTab === "suco") {
            cols.push({ key: "suco", label: "Suco", sortable: true, align: "left" });
        }
        cols.push({
            key: "displayValue",
            label: activeParamConfig.label,
            sortable: false,
            align: "center",
            render: (row) => (
                <input
                    type="number"
                    className="fva-value-input"
                    style={{ backgroundColor: getBadgeColor(selectedParameter, row.displayValue, row.apiColor) }}
                    value={row.displayValue ?? ""}
                    onFocus={handleVaInputFocus}
                    onBlur={handleVaInputBlur}
                    onKeyDown={handleVaInputKeyDown}
                    onChange={(e) =>
                        setVaOverrides((prev) => ({ ...prev, [row.overrideKey]: e.target.value }))
                    }
                />
            ),
        });
        return cols;
    }, [adminLevelTab, selectedParameter, activeParamConfig.label, handleVaInputFocus, handleVaInputBlur, handleVaInputKeyDown]);

    // GeoJSON key: remount when date changes or data is refreshed
    const vaMapGeoKey = `va-geo-${vaSelectedDate}-${vaGeoRefreshKey}`;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Paper
            elevation={0}
            sx={{ p: 1.5, borderRadius: "16px", border: "1px solid #e2e8f0" }}
            className="forecast-value-addition"
        >
            {/* <Typography sx={{ color: "#1e293b", fontWeight: 700, fontSize: "18px", mb: 1.5 }}>
                Forecast Value Addition
            </Typography> */}

            <Alert
                severity="warning"
                icon={false}
                sx={{
                    mb: 2.5,
                    borderRadius: "10px",
                    border: "1px solid #fbbf24",
                    backgroundColor: "#fffbeb",
                    fontSize: "12.5px",
                    color: "#92400e",
                    "& .MuiAlert-message": { width: "100%" },
                    py: 1,
                    px: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <span style={{ fontSize: "15px", lineHeight: 1.4, flexShrink: 0 }}>⚠️</span>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "12.5px", color: "#92400e", mb: 0.3 }}>
                            Important
                        </Typography>
                        <Typography sx={{ fontSize: "12.5px", color: "#92400e", lineHeight: 1.55 }}>
                            All forecast value additions or modifications must be finalized before{" "}
                            <strong>08:30 AM TLT</strong>. The automated weather alert dissemination emails are
                            generated and sent at <strong>08:30 AM TLT</strong>. Updates made after 08:30 AM TLT
                            will not be included in the automated dissemination, requiring the weather advisory to
                            be shared manually.
                        </Typography>
                    </Box>
                </Box>
            </Alert>

            {/* ── Top control row: tabs left | parameter + days right ───────── */}
            <div className="fva-controls-row">
                <Tabs
                    value={adminLevelTab}
                    onChange={(_, v) => setAdminLevelTab(v)}
                    sx={{
                        minHeight: 38,
                        "& .MuiTab-root": { minHeight: 38, py: 0, fontSize: "13px", textTransform: "none", fontWeight: 600 },
                    }}
                >
                    {LEVEL_TABS.map((t) => (
                        <Tab key={t.value} value={t.value} label={t.label} />
                    ))}
                </Tabs>

                <div className="fva-right-controls">
                    <div className="fva-select-group" style={{ minWidth: 195 }}>
                        <label className="fva-select-label">Parameter: </label>
                        <select
                            className="fva-select-input"
                            value={selectedParameter}
                            onChange={(e) => setSelectedParameter(e.target.value)}
                        >
                            {PARAMETERS.map((p) => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="fva-day-buttons">
                        {[0, 1, 2, 3, 4].map((idx) => (
                            <button
                                key={idx}
                                className={`fva-day-btn${selectedDayIndex === idx ? " active" : ""}`}
                                onClick={() => setSelectedDayIndex(idx)}
                            >
                                Day {idx + 1}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── 2 × 2 comparison grid ─────────────────────────────────────── */}
            <div className="fva-comparison-grid">
                {SOURCES.map((source) => (
                    <SourceMiniMap
                        key={source}
                        source={source}
                        geoData={activeGeo}
                        rows={sourceRows[source] || []}
                        paramId={selectedParameter}
                        loading={sourceLoading[source] === true}
                        errorMsg={sourceError[source] || null}
                        initTime={sourceData[source]?.latest_init_time}
                        mapKey={`mini-${source}-${activeLevelConfig.geoKey}`}
                        geoDataKey={`mini-geo-${source}-${activeLevelConfig.geoKey}-d${selectedDayIndex}`}
                    />
                ))}
            </div>

            <Divider sx={{ my: 3 }} />

            {/* ── Value addition (collapsible) ───────────────────────────────── */}
            <div className="fva-va-section">
                <button className="fva-va-toggle" onClick={() => setVaExpanded((v) => !v)}>
                    <span className="fva-va-toggle-label">Value Addition — Click Here</span>
                    <span className="fva-va-toggle-icon">{vaExpanded ? "▲" : "▼"}</span>
                </button>

                {vaExpanded && (
                    <div className="fva-va-content">
                        {/* Form row */}
                        <div className="fva-va-form-row">
                            <div className="fva-select-group">
                                <label className="fva-select-label">Source</label>
                                <div className="fva-select-static">{vaSource}</div>
                            </div>

                            {vaData && vaAvailableDates.length > 0 && (
                                <div className="fva-select-group">
                                    <label className="fva-select-label">Forecast Date</label>
                                    <select
                                        className="fva-select-input"
                                        value={vaSelectedDate}
                                        onChange={(e) => {
                                            setVaSelectedDate(e.target.value);
                                            setVaOverrides({});
                                            setVaGeoRefreshKey((k) => k + 1);
                                        }}
                                    >
                                        {vaAvailableDates.map((d, i) => (
                                            <option key={d} value={d}>
                                                Day {i + 1} — {formatDayLabel(d)} ({d})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {vaLoading && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b", fontSize: "13px" }}>
                                    <CircularProgress size={13} />
                                    Loading...
                                </Box>
                            )}
                        </div>

                        {vaError && (
                            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{vaError}</Alert>
                        )}

                        {vaLoading && (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {vaData && !vaLoading && vaSelectedDate && (
                            <>
                                {/* Value addition map with filled polygons + draw tool */}
                                <Box sx={{ mt: 2, mb: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                                            Map — {activeParamConfig.label} · {activeLevelConfig.label} · Day {vaAvailableDates.indexOf(vaSelectedDate) + 1} ({vaSelectedDate})
                                        </Typography>
                                        {vaSelectionCodes && (
                                            <Typography sx={{ fontSize: "11px", color: "#2563eb", fontWeight: 600 }}>
                                                Delete a drawn shape on the map to remove it from the list below
                                            </Typography>
                                        )}
                                    </Box>

                                    {vaPolygons.length > 0 && (
                                        <div className="fva-poly-panel">
                                            <div className="fva-poly-panel-header">
                                                <div className="fva-poly-summary">
                                                    <span className="fva-poly-count-badge">{vaPolygons.length}</span>
                                                    <span>
                                                        polygon{vaPolygons.length === 1 ? "" : "s"} drawn ·{" "}
                                                        <strong>{vaSelectionCodes?.size ?? 0}</strong> area{(vaSelectionCodes?.size ?? 0) === 1 ? "" : "s"} total
                                                    </span>
                                                </div>
                                                <div className="fva-mode-toggle">
                                                    <button
                                                        type="button"
                                                        className={`fva-mode-btn${!vaApplyAll ? " active" : ""}`}
                                                        onClick={() => setVaApplyAll(false)}
                                                    >
                                                        Each Polygon
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`fva-mode-btn${vaApplyAll ? " active" : ""}`}
                                                        onClick={() => setVaApplyAll(true)}
                                                    >
                                                        Same Value — All
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="fva-poly-list">
                                                {vaApplyAll ? (
                                                    <div className="fva-poly-row">
                                                        <div className="fva-poly-row-label">
                                                            <span className="fva-poly-badge fva-poly-badge-all">All</span>
                                                            <span>Apply to all {vaPolygons.length} polygons</span>
                                                        </div>
                                                        <div className="fva-poly-row-controls">
                                                            <input
                                                                type="number"
                                                                className="fva-poly-value-input"
                                                                placeholder="Value"
                                                                value={vaAllValue}
                                                                onChange={(e) => setVaAllValue(e.target.value)}
                                                            />
                                                            <div className="fva-op-toggle">
                                                                <button
                                                                    type="button"
                                                                    className={`fva-op-btn add${vaAllOp === "add" ? " active" : ""}`}
                                                                    onClick={() => handleVaAllOpToggle("add")}
                                                                >
                                                                    + Add
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`fva-op-btn sub${vaAllOp === "sub" ? " active" : ""}`}
                                                                    onClick={() => handleVaAllOpToggle("sub")}
                                                                >
                                                                    − Sub
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    vaPolygons.map((poly, idx) => (
                                                        <div className="fva-poly-row" key={poly.id}>
                                                            <div className="fva-poly-row-label">
                                                                <span className="fva-poly-badge">{idx + 1}</span>
                                                                <span>Polygon {idx + 1}</span>
                                                                <span className="fva-poly-area-chip">
                                                                    {poly.codes.size} area{poly.codes.size === 1 ? "" : "s"}
                                                                </span>
                                                            </div>
                                                            <div className="fva-poly-row-controls">
                                                                <input
                                                                    type="number"
                                                                    className="fva-poly-value-input"
                                                                    placeholder="Value"
                                                                    value={poly.value}
                                                                    onChange={(e) => handleVaPolygonValueChange(poly.id, e.target.value)}
                                                                />
                                                                <div className="fva-op-toggle">
                                                                    <button
                                                                        type="button"
                                                                        className={`fva-op-btn add${poly.op === "add" ? " active" : ""}`}
                                                                        onClick={() => handleVaPolygonOpToggle(poly.id, "add")}
                                                                    >
                                                                        + Add
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`fva-op-btn sub${poly.op === "sub" ? " active" : ""}`}
                                                                        onClick={() => handleVaPolygonOpToggle(poly.id, "sub")}
                                                                    >
                                                                        − Sub
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            <div className="fva-poly-panel-footer">
                                                <button
                                                    type="button"
                                                    className="fva-bulk-update-btn"
                                                    onClick={handleVaPolygonUpdate}
                                                    disabled={!hasPendingPolygonUpdates}
                                                >
                                                    Apply Update
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <ValueAdditionMap
                                        geoData={vaGeo}
                                        rows={vaRows}
                                        paramId={selectedParameter}
                                        geoKey={vaMapGeoKey}
                                        featureGroupRef={vaFeatureGroupRef}
                                        onDrawCreated={handleVaDrawCreated}
                                        onDrawDeleted={handleVaDrawDeleted}
                                        selectionCodes={vaSelectionCodes}
                                    />
                                </Box>

                                {/* Editable table */}
                                <Box sx={{ mt: 3 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1.5,
                                            flexWrap: "wrap",
                                            gap: 1,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                                            Edit Forecast Values — {activeParamConfig.label} ({vaSelectedDate})
                                            {vaSelectionCodes && ` · ${vaSelectionCodes.size} selected`}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<RestartAlt />}
                                                onClick={handleVaReset}
                                                disabled={vaSaving || !hasCurrentOverrides}
                                                sx={{
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderColor: "#dc2626",
                                                    color: "#dc2626",
                                                    "&:hover": { borderColor: "#b91c1c", backgroundColor: "#fef2f2" },
                                                }}
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={vaSaving ? null : <Save />}
                                                onClick={handleVaSave}
                                                disabled={vaSaving || !vaSelectedDate || !hasCurrentOverrides}
                                                sx={{
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    backgroundColor: "#16a34a",
                                                    "&:hover": { backgroundColor: "#15803d" },
                                                }}
                                            >
                                                {vaSaving ? "Saving..." : "Save"}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <CommonTable
                                        tableData={displayVaRows}
                                        columns={vaTableColumns}
                                        tableName={`VA_${vaSource}_${adminLevelTab}_${selectedParameter}`}
                                        searchableKeys={["municipality", "subMunicipality", "suco"]}
                                        searchPlaceholder="Search by municipality, sub municipality, or suco..."
                                        emptyMessage="No forecast data for this selection."
                                        rowIdKey="admCode"
                                    />
                                </Box>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* ── Snackbar notification ────────────────────────────────────── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ForecastValueAddition;
